import { Tag } from 'antd';
import axios from 'axios';
import { ActionStatus } from 'mdello-client-ts';
import { HolidayType } from 'mdello-client-ts/dist/src/interfaces/providers/intranet/holiday/holiday';
import { useEffect, useState } from 'react';
import Copy from 'src/component/Copy';
import InnerHeader from 'src/component/InnerHeader';
import NoxTable from 'src/component/Table';
import formatDate from 'src/helpers/formatDate';
import { VacationTypes } from 'src/reactQuery/requests/enums/requestCategories.enum';
import { useDeleteVacations } from 'src/reactQuery/requests/vacations/useDeleteRequests';
import { useDownloadVacations } from 'src/reactQuery/requests/vacations/useDownloadCsvMutation';
import { useGetVacations } from 'src/reactQuery/requests/vacations/useGetRequests';
import { useGetHolidays } from 'src/reactQuery/requests/vacations/useGetVacationDays';
import { useUpdateVacations } from 'src/reactQuery/requests/vacations/useUpdateRequests';
import CButton from '../../component/Button';
import Modal from '../../component/Modal/Modal';
import { AutocompleteSelect } from '../../component/Select';
import { LocalStorageKey } from '../../localStorage/enums/localStorageKey.enum';
import { getDataFromLocalStorage } from '../../localStorage/getDataFromLocalStorage';

interface RequestTableProps {
  _id: string;
  comment: string;
  company: string;
  createdAt: Date;
  description: string;
  images: string[];
  key: number;
  remainingDays: number;
  requestDate: Date;
  startDate: string;
  status: ActionStatus;
  type: 'ongoing' | 'history';
  updatedAt: Date;
  userName: string;
  username: string;
  vacationEndDate: Date;
  vacationStartDate: Date;
  vacationTitle: VacationTypes;
  vacationType: HolidayType;
}

interface TableActionsProps {
  onClick: () => void;
  title: string;
  type: string;
}

const Vacations = () => {
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [company, setCompany] = useState('');
  const [department, setDepartment] = useState('');
  const [reportType, setReportType] = useState('');
  const [year, setYear] = useState('');
  const [month, setMonth] = useState('');
  const [requests, setRequests] = useState<RequestTableProps[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[] | undefined>([]);
  const [reportParams, setReportParams] = useState<any>({});
  const { data: VacationsRequestData, refetch } = useGetVacations('vacation');
  const { data: holidays } = useGetHolidays(HolidayType.HOLIDAY);
  const { mutate: deleteMutation } = useDeleteVacations({
    onSuccess: () => {
      refetch();
    }
  });
  const { mutate: updateMutation } = useUpdateVacations({
    onSuccess: () => {
      refetch();
    }
  });

  const { mutate: onDownloadCsvMutation } = useDownloadVacations({});

  useEffect(() => {
    if (!VacationsRequestData || !holidays) {
      return;
    }

    // mapToTable
    // @ts-ignore
    const filteredRequests: RequestTableProps[] = VacationsRequestData.data.map(
      (request, index) => {
        const {
          type,
          duration,
          requestDate,
          startDate,
          endDate,
          vacationType,
          comment,
          vacationTitle,
          images
        } = request.data;
        const holidayData = holidays?.find((holiday) => holiday.user._id === request.user);
        if (!holidayData) {
          return;
        }

        const { company, startDate: holidayStartDate, user } = holidayData!;

        const { _id, createdAt, status, description, username, updatedAt } = request;

        return {
          _id,
          comment,
          company,
          createdAt,
          description,
          duration,
          images,
          key: index,
          requestDate,
          startDate: holidayStartDate,
          status,
          type,
          updatedAt,
          userName: user.fullName,
          username,
          vacationEndDate: endDate,
          vacationStartDate: startDate,
          vacationTitle,
          vacationType
        };
      }
    );
    setRequests(filteredRequests.filter((request) => request !== undefined));
  }, [VacationsRequestData, holidays]);

  useEffect(() => {
    if (!reportParams) {
      return;
    }

    getReportParams();
  }, []);

  const columns = [
    {
      dataIndex: '_id',
      key: 'id',
      render: (_id: any) => {
        return <Copy text={_id} />;
      },
      sorter: (a: RequestTableProps, b: RequestTableProps) => a._id.localeCompare(b._id),
      title: 'Id'
    },
    {
      dataIndex: 'vacationTitle',
      key: 'vacationTitle',
      render: (vacationTitle: any) => {
        return <Copy text={vacationTitle?.toString()} />;
      },
      sorter: (a: RequestTableProps, b: RequestTableProps) =>
        a.vacationTitle.toString().localeCompare(b.vacationTitle),
      title: 'Vacation Title'
    },
    {
      dataIndex: 'company',
      key: 'company',
      render: (company: any) => {
        return <Copy text={company?.toString()} />;
      },
      sorter: (a: RequestTableProps, b: RequestTableProps) =>
        a.company.toString().localeCompare(b.company),
      title: 'Company'
    },
    {
      dataIndex: 'startDate',
      key: 'startDate',
      render: (startDate: any) => {
        return <Copy text={startDate?.toString()} />;
      },
      sorter: (a: RequestTableProps, b: RequestTableProps) =>
        a.startDate.toString().localeCompare(b.startDate),
      title: 'Work start date'
    },
    {
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt: any) => {
        return <Copy text={formatDate(createdAt)} />;
      },
      sorter: (a: RequestTableProps, b: RequestTableProps) =>
        a.createdAt.toString().localeCompare(b.createdAt.toString()),
      title: 'Vacation Request Date'
    },
    {
      dataIndex: 'description',
      key: 'description',
      render: (description: any) => {
        return <Copy text={description} />;
      },
      sorter: (a: RequestTableProps, b: RequestTableProps) =>
        a.description.toString().localeCompare(b.description),
      title: 'Description'
    },
    {
      dataIndex: 'userName',
      key: 'userName',
      render: (userName: any) => {
        return <Copy text={userName} />;
      },
      sorter: (a: RequestTableProps, b: RequestTableProps) =>
        a.userName.toString().localeCompare(b.userName),
      title: 'User Name'
    },
    {
      dataIndex: 'status',
      key: 'status',
      render: (status: any) => {
        let style = {};
        if (status === 'pending') {
          style = {
            backgroundColor: 'rgba(155, 160, 173, 0.15)',
            color: '#9BA0AD'
          };
        }
        if (status === 'confirmed') {
          style = {
            backgroundColor: 'rgba(65, 204, 156, 0.15)',
            color: '#41CC9C'
          };
        }
        if (status === 'rejected') {
          style = {
            backgroundColor: 'rgba(255, 71, 108, 0.15)',
            color: '#FF476C'
          };
        }
        return (
          <Tag
            key={status}
            style={{
              ...style,
              border: 'none',
              cursor: 'default',
              width: '90%'
            }}
          >
            {status}
          </Tag>
        );
      },
      sorter: (a: RequestTableProps, b: RequestTableProps) =>
        a.status.toString().localeCompare(b.status),
      title: 'Status'
    },
    {
      dataIndex: 'vacationStartDate',
      key: 'vacationStartDate',
      render: (vacationStartDate: any) => {
        return <Copy text={formatDate(vacationStartDate)} />;
      },
      sorter: (a: RequestTableProps, b: RequestTableProps) =>
        a.vacationStartDate.toString().localeCompare(b.vacationStartDate.toString()),
      title: 'Vacation Start Date'
    },
    {
      dataIndex: 'vacationEndDate',
      key: 'vacationEndDate',
      render: (vacationEndDate: any) => {
        return <Copy text={formatDate(vacationEndDate)} />;
      },
      sorter: (a: RequestTableProps, b: RequestTableProps) =>
        a.vacationEndDate.toString().localeCompare(b.vacationEndDate.toString()),
      title: 'Vacation End Date'
    },
    {
      dataIndex: 'vacationType',
      key: 'vacationType',
      render: (vacationType: any) => {
        return <Copy text={vacationType} />;
      },
      sorter: (a: RequestTableProps, b: RequestTableProps) =>
        a.vacationType.toString().localeCompare(b.vacationType),
      title: 'Vacation type'
    },
    {
      dataIndex: 'images',
      key: 'images',
      render: (images: any) => {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {images?.map((image: any, index: number) => {
              return (
                <a key={index} href={image.src} rel="noopener noreferrer" target="_blank">
                  Link
                </a>
              );
            })}
          </div>
        );
      },
      title: 'Attachment'
    }
  ];

  const onRequestDelete = () => {
    selectedRowKeys?.forEach((idx) => {
      const item = VacationsRequestData?.data[idx];

      if (item) {
        deleteMutation(item._id);
      }
    });

    setSelectedRowKeys([]);
  };
  const onApprove = () => {
    selectedRowKeys?.forEach((idx) => {
      const item = VacationsRequestData?.data[idx];

      if (item) {
        updateMutation({
          requestId: item._id,
          status: ActionStatus.CONFIRMED
        });
      }
    });

    setSelectedRowKeys([]);
  };
  const onReject = () => {
    selectedRowKeys?.forEach((idx) => {
      const item = VacationsRequestData?.data[idx];

      if (item) {
        updateMutation({
          requestId: item._id,
          status: ActionStatus.REJECTED
        });
      }
    });

    setSelectedRowKeys([]);
  };

  const onDownloadHRReports = () => {
    setShowDownloadModal(true);
  };

  const actions: TableActionsProps[] = [
    {
      onClick: () => onDownloadHRReports(),
      title: 'Download HR Report',
      type: 'primary'
    },
    {
      onClick: () => onDownloadCsvMutation(),
      title: 'Download CSV',
      type: 'primary'
    }
  ];

  if (selectedRowKeys?.length) {
    actions.unshift(
      {
        onClick: onRequestDelete,
        title: 'Delete',
        type: ''
      },
      {
        onClick: onApprove,
        title: 'Approve',
        type: 'secondaryModified'
      },
      {
        onClick: onReject,
        title: 'Reject',
        type: 'secondaryModified'
      }
    );
  }

  const onDownloadCancel = () => {
    setShowDownloadModal(false);
    setCompany('');
    setDepartment('');
    setYear('');
    setMonth('');
  };

  const onDownload = async () => {
    const currentProject: any = getDataFromLocalStorage(LocalStorageKey.CURRENT_PROJECT);
    const currentSession = getDataFromLocalStorage(LocalStorageKey.SESSION_ID);

    const response = await axios.get(
      `https://m7.mdello.com/api_admin/v1/intranet/holiday/hr-report`,
      {
        headers: {
          'Content-Type': 'application/json',
          'x-session-id': currentSession
        },
        params: {
          company,
          department,
          month: months.indexOf(month),
          projectId: currentProject._id,
          reportType,
          year: parseInt(year, 10)
        },
        responseType: 'arraybuffer'
      }
    );

    setShowDownloadModal(false);
    setCompany('');
    setDepartment('');
    setReportType('');
    setYear('');
    setMonth('');

    // @ts-ignore
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `hr-report-${month}.xlsx`);
    document.body.appendChild(link);
    link.click();

    return response.data;
  };

  const modalStyle = {
    '& .MuiDialogContent-root': {
      display: 'flex',
      flexDirection: 'column',
      height: '290px',
      justifyContent: 'space-evenly',
      paddingTop: '0px'
    },
    width: '400px'
  };

  const getReportParams = async () => {
    const currentProject: any = getDataFromLocalStorage(LocalStorageKey.CURRENT_PROJECT);
    const currentSession = getDataFromLocalStorage(LocalStorageKey.SESSION_ID);

    const response = await axios.get(
      `https://m7.mdello.com/api_admin/v1/intranet/holiday/hr-report/params`,

      {
        headers: {
          'Content-Type': 'application/json',

          'x-session-id': currentSession
        },

        params: {
          projectId: currentProject._id
        }
      }
    );

    setReportParams(response.data.data);
  };

  const companyList =
    reportParams.companies?.map((company: any) => {
      return { key: company.name.trim(), value: company.name.trim() };
    }) || [];

  const startYear = 2023;
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - startYear + 1 }, (_, index) =>
    String(startYear + index)
  );
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];

  const getDepartments = () => {
    return (
      reportParams.companies
        ?.find((item: any) => item.name === company)
        ?.departments?.map((department: any) => department.name) || []
    );
  };

  const getReportTypes = () => {
    return ['Management GRE', 'Management m2', 'Management m2 Maintenance'];
  };

  const isReportDownloadEnabled = year && month;

  return (
    <div
      style={{
        backgroundColor: 'white',
        height: 'calc(100vh - 155px)',
        overflowY: 'scroll'
      }}
    >
      <InnerHeader subtitle={VacationsRequestData?.paging?.total} title="Vacations" />
      <NoxTable
        actions={actions}
        columns={columns}
        data={requests}
        // @ts-ignore
        searchBy="userName"
        // @ts-ignore
        selectedRowKeys={selectedRowKeys}
        // @ts-ignore
        setSelectedRowKeys={setSelectedRowKeys}
        totalPages={VacationsRequestData?.paging?.total}
      />
      <Modal
        footerActions={
          <>
            <CButton onClick={onDownloadCancel} type="secondary" xstyle={{ height: '40px' }}>
              Cancel
            </CButton>
            <CButton
              disabled={!isReportDownloadEnabled}
              onClick={onDownload}
              type={isReportDownloadEnabled ? 'primary' : 'secondary'}
              xstyle={{ height: '40px' }}
            >
              Download
            </CButton>
          </>
        }
        handleClose={onDownloadCancel}
        isModalOpen={showDownloadModal}
        styles={modalStyle}
        titleContent={<p className="modal-title">Download HR Report</p>}
      >
        <AutocompleteSelect
          getOptionLabel={(option) => option}
          id="autocomplete-ProjectBlock"
          onSelect={(value: string) => {
            setCompany(value);
            setDepartment('');
          }}
          onViewAll={null}
          options={companyList.map((obj: any) => obj.value)}
          placeholderValue={company || 'Select Company'}
          styles={
            {
              // width: '175px'
            }
          }
        />
        <AutocompleteSelect
          getOptionLabel={(option) => option}
          id="autocomplete-ProjectBlock"
          onSelect={(value: string) => setDepartment(value)}
          onViewAll={null}
          options={getDepartments()}
          placeholderValue={department || 'Select Department'}
          styles={
            {
              // width: '175px'
            }
          }
        />
        <AutocompleteSelect
          getOptionLabel={(option) => option}
          id="autocomplete-ProjectBlock"
          onSelect={(value: string) => setReportType(value)}
          onViewAll={null}
          options={getReportTypes()}
          placeholderValue={reportType || 'Select Report Type'}
          styles={
            {
              // width: '175px'
            }
          }
        />
        <AutocompleteSelect
          getOptionLabel={(option) => option}
          id="autocomplete-year"
          onSelect={(value: string) => setYear(value)}
          onViewAll={null}
          options={years}
          placeholderValue={year || 'Select Year'}
          styles={
            {
              // width: '175px'
            }
          }
        />
        <AutocompleteSelect
          getOptionLabel={(option) => option}
          id="autocomplete-month"
          onSelect={(value: string) => setMonth(value)}
          onViewAll={null}
          options={months}
          placeholderValue={month || 'Select Month'}
          styles={
            {
              // width: '175px'
            }
          }
        />
      </Modal>
    </div>
  );
};

export default Vacations;
